import { FALLBACK_TIMEZONE, isoDateInTz, formatTimeInTz } from "@/design-system/lib/format";

/**
 * Calendar Appointments v1.0 — wall-clock ⇄ UTC helpers, native Date + Intl
 * only (no date library). The form lets a user enter a wall-clock date+time
 * which is conceptually *in their own timezone*; the wire format is a UTC
 * instant. These two helpers bridge that, and split a stored instant back
 * into the tz-local date/time inputs for editing.
 */

/**
 * Offset (in minutes) of `tz` from UTC at the given instant — i.e. how far
 * ahead of UTC the zone is. Computed by reading the instant's wall-clock in
 * `tz` via Intl and diffing it against the same wall-clock read as UTC.
 */
function wallClockAsUtcMs(instant: Date, tz: string): number {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const parts = dtf.formatToParts(instant);
  const get = (type: string) =>
    Number(parts.find((p) => p.type === type)?.value ?? "0");
  let hour = get("hour");
  // Intl renders midnight as "24" in some engines for hour12:false.
  if (hour === 24) hour = 0;
  return Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    hour,
    get("minute"),
    get("second"),
  );
}

function tzOffsetMinutes(instant: Date, tz: string): number {
  return Math.round((wallClockAsUtcMs(instant, tz) - instant.getTime()) / 60000);
}

/**
 * The two-pass convergence: treat the wall clock as if it were UTC, correct by
 * the offset at that guess, then re-correct. Two passes are enough because the
 * second guess is already within an hour of the answer, so it reads the right
 * side of any transition. Split out so the gap handling below can run it twice
 * — once to discover that a wall clock does not exist, and once on the shifted
 * clock that does.
 */
function convergeToUtcMs(wallClockAsUtc: number, zone: string): number {
  let utcMs = wallClockAsUtc - tzOffsetMinutes(new Date(wallClockAsUtc), zone) * 60000;
  return wallClockAsUtc - tzOffsetMinutes(new Date(utcMs), zone) * 60000;
}

/**
 * Convert a wall-clock `YYYY-MM-DD` date + `HH:mm` time, interpreted in `tz`,
 * to a UTC ISO instant. Two passes converge across a DST boundary (the
 * offset depends on the instant we're solving for).
 *
 * 🔴 A REPEATING APPOINTMENT DOES NOT USE THIS HELPER, and that divergence is
 * deliberate. The recurrence wire format carries the wall clock itself — an
 * anchor date, an anchor time and an IANA zone id — and the SERVER derives
 * every occurrence's instant from them, using `TimeZoneInfo.IsInvalidTime` /
 * `IsAmbiguousTime` to resolve the two days a year a wall clock is not a single
 * instant. See `RecurrenceExpander.ToUtcInZone` for that side. Do not
 * "simplify" the recurrence payload into a UTC instant to make the two paths
 * share one code path: the server has `TimeZoneInfo`, this has `Intl`, and the
 * two wire formats carry genuinely different things.
 *
 * <b>The two DST seams, and which one used to be wrong.</b> Both are now
 * resolved to the same answer the server reaches, by the same rule.
 *
 * SPRING FORWARD (the gap). On 2026-03-08 in America/New_York the wall clock
 * 02:30 does not exist — clocks jump 02:00 EST straight to 03:00 EDT. The
 * convergence alone landed on `06:30Z`, which reads back in that zone as
 * **01:30**: an hour EARLY, on the wrong side of the gap. It now detects the
 * gap by ROUND-TRIP — converge, read the instant back in the zone, and if the
 * wall clock that comes back is not the one that went in, the input never
 * existed — then shifts the clock FORWARD by the measured length of the gap
 * and converges again. `RecurrenceExpander.ToUtcInZone` does exactly this
 * (`IsInvalidTime` → add `after - before`), so 02:30 becomes 03:30 and a 2:30am
 * standing meeting happens 30 minutes into the new day's clock rather than
 * silently landing an hour early.
 *
 * 🔴 THE OLD BEHAVIOUR WAS NOT UNIFORMLY "AN HOUR EARLY", AND THE ZONES WHERE
 * IT LOOKED RIGHT WERE RIGHT BY COINCIDENCE. Which way the convergence fell
 * depended on the SIGN of the zone's offset. Measured on the unfixed helper,
 * every case a wall clock in the gap:
 *
 *   America/New_York  02:30 →  06:30Z  reads back 01:30   ← an hour EARLY
 *   Europe/London     01:30 →  01:30Z  reads back 02:30   ← already forward
 *   Europe/Berlin     02:30 →  01:30Z  reads back 03:30   ← already forward
 *   Australia/Sydney  02:30 → Oct 3 16:30Z reads back 03:30 ← already forward
 *
 * So only the Americas were visibly wrong, and the three that agreed with the
 * server did so because a positive-offset zone's second pass happens to
 * overshoot the other way — not because anything decided it. Replacing that
 * coincidence with the rule leaves those three byte-identical and corrects
 * New York; a test pins all four, so a later refactor cannot quietly restore
 * the asymmetry.
 *
 * FALL BACK (the ambiguity). On 2026-11-01 in America/New_York 01:30 happens
 * twice. The convergence picks `05:30Z` = 01:30 **EDT**, the EARLIER of the
 * two, which is what the server takes via `offsets.Max()` (the larger UTC
 * offset is the earlier instant). This path was ALREADY correct and is
 * deliberately left alone — an ambiguous clock round-trips, so the gap
 * detection above does not fire on it. Pinned, because the tempting
 * "handle both seams symmetrically" edit would change an answer that is right.
 */
export function wallClockToUtcIso(
  date: string,
  time: string,
  tz?: string | null,
): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  const t = /^\d{2}:\d{2}$/.test(time) ? time : "00:00";
  const zone = tz || FALLBACK_TIMEZONE;
  const [y, m, d] = date.split("-").map(Number);
  const [hh, mm] = t.split(":").map(Number);
  // The wall clock treated as if it were UTC. `convergeToUtcMs` turns that
  // into the real instant for an ordinary clock; the gap arm below is what
  // handles a clock that has no instant at all.
  const guess = Date.UTC(y, m - 1, d, hh, mm, 0);
  const utcMs = convergeToUtcMs(guess, zone);

  // Did the wall clock we asked for survive the round trip? If reading the
  // instant back in the zone gives a DIFFERENT wall clock, the one we were
  // handed never existed — we are inside a spring-forward gap. This is the
  // Intl-only equivalent of `TimeZoneInfo.IsInvalidTime`, and it cannot
  // false-positive on the fall-back seam: an ambiguous clock is a real clock,
  // so it round-trips.
  if (wallClockAsUtcMs(new Date(utcMs), zone) === guess) return new Date(utcMs).toISOString();

  // The gap's length is the difference between the offsets safely either side
  // of it — a day out in each direction, which stays clear of the transition
  // without needing the zone's adjustment rule. Mirrors the server's
  // `after - before`, including its one-hour floor for a zone whose probes
  // disagree in the other direction.
  const DAY_MS = 86400000;
  const before = tzOffsetMinutes(new Date(utcMs - DAY_MS), zone);
  const after = tzOffsetMinutes(new Date(utcMs + DAY_MS), zone);
  const gapMinutes = after - before;
  const shifted = guess + (gapMinutes > 0 ? gapMinutes : 60) * 60000;
  return new Date(convergeToUtcMs(shifted, zone)).toISOString();
}

/** The `YYYY-MM-DD` value of a stored instant as observed in `tz`. */
export function utcIsoToDateInput(
  iso: string | null | undefined,
  tz?: string | null,
): string {
  return isoDateInTz(iso, tz);
}

/** The 24h `HH:mm` value of a stored instant as observed in `tz`. */
export function utcIsoToTimeInput(
  iso: string | null | undefined,
  tz?: string | null,
): string {
  if (!iso) return "";
  // `formatTimeInTz` with hour12:false + 2-digit hour yields "HH:mm".
  const formatted = formatTimeInTz(iso, tz, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  if (!formatted) return "";
  // Normalize a "24:mm" midnight rendering down to "00:mm".
  const [h, m] = formatted.split(":");
  const hour = h === "24" ? "00" : h;
  return `${hour}:${m}`;
}

/**
 * The shape `appointmentDayZone` needs. Structural rather than `AppointmentDto`
 * so the form can ask about state it has not saved yet (a user who has just
 * ticked "All day" on a timed appointment), and so this module keeps no
 * dependency on the API types.
 */
export interface AppointmentDayFacts {
  isAllDay: boolean;
  authoredTimeZoneId?: string | null;
}

/**
 * The zone an appointment's calendar DAY is written in — the one place that
 * rule is stated, and the one every surface that reads a day must call.
 *
 * 🔴 TWO KINDS OF VALUE SHARE ONE COLUMN, WHICH IS THE WHOLE PROBLEM. A TIMED
 * appointment is a real instant, so its day is whatever day it is where the
 * VIEWER is standing — a 9am Phoenix call is genuinely tomorrow for a Tokyo
 * colleague, and rendering it on tomorrow is correct. An ALL-DAY appointment is
 * a wall-calendar value that happens to be STORED as two instants (authored
 * midnight through 23:59), so its day is a fact about the author's calendar and
 * does not move for anybody. The 20th is the 20th everywhere.
 *
 * Read the second kind in the viewer's zone and it shifts: `startAt` lands a day
 * EARLY for every viewer west of the author, and `endAt` a day LATE for every
 * viewer east of them. Not a rounding artefact and not an edge case — it is
 * every such viewer, always, and it is why a one-day appointment can read as two.
 *
 * 🔴 THE DOMESTIC CASE IS THE ONE THAT GETS MISREPORTED. Los Angeles is PDT
 * (−7) in September and agrees with Phoenix; it is PST (−8) in January and does
 * not. A US-only team sees all-day appointments shift by a day from November
 * through March and not at all in summer, which reads as intermittent rather
 * than as a rule.
 *
 * NULL FALLS BACK TO THE VIEWER, deliberately. `authoredTimeZoneId` is null for
 * every row written before the server began capturing it, and it was never
 * backfilled — the authoring zone is not recoverable from the row, so writing
 * the organizer's current zone into old rows would freeze a value that may
 * already be wrong. Falling back to the viewer's zone is byte-for-byte what
 * every surface did before this helper existed, so a historical row renders
 * exactly as it always has and only future drift is prevented.
 *
 * 🔴 HYDRATION AND COMPOSE MUST PASS THE SAME FACTS. The edit form reads a day
 * out of a stored instant and writes one back; if the two halves resolve
 * different zones, a save that changed nothing but the title MOVES the
 * appointment. That is worse than the bug this closes, so the form derives one
 * value and uses it on both sides.
 */
export function appointmentDayZone(
  appointment: AppointmentDayFacts | null | undefined,
  viewerTz: string,
): string {
  if (!appointment?.isAllDay) return viewerTz;
  return appointment.authoredTimeZoneId?.trim() || viewerTz;
}
